import { AppErrorCode, AppError } from '../types';

export const ERROR_MESSAGES_AR: Record<AppErrorCode, string> = {
  SEAT_ALREADY_BOOKED: 'عذرًا، تم حجز هذا المقعد للتو من قبل طالب آخر. يرجى اختيار مقعد آخر متاح.',
  SEAT_HELD_BY_ANOTHER: 'المقعد قيد الحجز المؤقت بواسطة طالب آخر حاليًا. يرجى الانتظار دقيقتين أو اختيار مقعد آخر.',
  HOLD_EXPIRED: 'انتهت مدة حجز المقعد (5 دقائق). يرجى اختيار المقعد مرة أخرى للمتابعة.',
  TRIP_NOT_FOUND: 'الرحلة المطلوبة غير موجودة أو تم إلغاؤها.',
  TRIP_FULL: 'عذرًا، اكتملت جميع مقاعد هذه الرحلة بالكامل. يمكنك تقديم طلب رحلة جديدة لموعدك المفضل.',
  TRIP_NOT_OPEN: 'هذه الرحلة غير متاحة للحجز حاليًا.',
  INVALID_PHONE_NUMBER: 'يرجى إدخال رقم هاتف محمول صحيح (مكون من 11 رقمًا).',
  INVALID_SEAT_NUMBER: 'رقم المقعد المحدد غير صالح (يجب أن يكون بين 1 و 50).',
  UNAUTHORIZED_ADMIN: 'غير مصرح لك بالدخول إلى لوحة التحكم الإدارية. يرجى تسجيل الدخول بحساب مسؤول معتمد.',
  UNAUTHENTICATED_STUDENT: 'يرجى تسجيل الدخول أولاً للوصول إلى هذه الصفحة أو حجز المقعد.',
  STUDENT_SUSPENDED: 'حسابك معلق حاليًا ولا يمكنك حجز رحلات جديدة. يرجى التواصل مع إدارة OTO للطلاب.',
  PROFILE_INCOMPLETE: 'يرجى استكمال بيانات ملفك الشخصي أولاً لتتمكن من متابعة حجز الرحلات.',
  UNAUTHORIZED_ACCESS: 'غير مصرح لك بالوصول إلى هذا الحجز أو التذكرة.',
  BOOKING_NOT_FOUND: 'لم يتم العثور على الحجز المطلوب في سجلات النظام.',
  BOOKING_ALREADY_CANCELLED: 'هذا الحجز ملغى بالفعل.',
  
  // Email + Password Authentication Errors
  AUTH_EMAIL_ALREADY_IN_USE: 'هذا البريد الإلكتروني مسجل بالفعل. يرجى تسجيل الدخول أو استخدام خيار استعادة كلمة المرور.',
  AUTH_INVALID_EMAIL: 'صيغة البريد الإلكتروني غير صحيحة. يرجى كتابة عنوان بريد إلكتروني صالح (مثال: student@example.com).',
  AUTH_WEAK_PASSWORD: 'كلمة المرور ضعيفة. يرجى استخدام كلمة مرور مكونة من 6 خانات على الأقل.',
  AUTH_INVALID_CREDENTIAL: 'البريد الإلكتروني أو كلمة المرور غير صحيحة. يرجى التحقق من صحة البيانات.',
  AUTH_WRONG_PASSWORD: 'كلمة المرور غير صحيحة. يرجى المحاولة مرة أخرى أو استعادة كلمة المرور.',
  AUTH_USER_NOT_FOUND: 'لا يوجد حساب مسجل بهذا البريد الإلكتروني. يرجى إنشاء حساب جديد.',
  AUTH_USER_DISABLED: 'تم تعطيل هذا الحساب من قبل الإدارة. يرجى التواصل مع الدعم الفني.',
  AUTH_TOO_MANY_REQUESTS: 'تم حظر المحاولات مؤقتًا لكثرة المحاولات الخاطئة. يرجى الانتظار بضع دقائق ثم المحاولة مجددًا.',
  AUTH_PASSWORDS_MISMATCH: 'كلمتا المرور غير متطابقتين. يرجى التأكد من كتابتهما بدقة.',
  AUTH_PASSWORD_RESET_SENT: 'تم إرسال رابط إعادة تعيين كلمة المرور إلى بريدك الإلكتروني بنجاح.',
  AUTH_NETWORK_FAILED: 'تعذر الاتصال بخدمة المصادقة. يرجى فحص اتصال الإنترنت والمحاولة ثانية.',
  
  NETWORK_ERROR: 'تعذر الاتصال بالخادم. يرجى التحقق من اتصال الإنترنت والمحاولة مرة أخرى.',
  TRANSACTION_ABORTED: 'تعذر إتمام عملية الحجز لتزامن طلبك مع حجز آخر. يرجى المحاولة مرة أخرى.',
  FIREBASE_UNAVAILABLE: 'الخدمة قيد الصيانة المؤقتة. يرجى المحاولة بعد قليل.',
  UNKNOWN_ERROR: 'حدث خطأ غير متوقع أثناء معالجة طلبك. يرجى التواصل مع الدعم الفني.',
};

export const createError = (code: AppErrorCode, details?: string): AppError => {
  return {
    code,
    messageAr: ERROR_MESSAGES_AR[code] || ERROR_MESSAGES_AR.UNKNOWN_ERROR,
    details,
  };
};

/**
 * Validates email address format
 */
export const validateEmail = (email: string): boolean => {
  const emailRegex = /^[^\s@]+@[^\s@]+\.[^\s@]+$/;
  return emailRegex.test(email.trim());
};

/**
 * Validates Egyptian mobile phone format if provided in profile
 */
export const validateEgyptianPhone = (phone: string): boolean => {
  if (!phone || phone.trim() === '') return true; // Optional profile field
  const cleaned = phone.trim().replace(/[\s-+()]/g, '');
  const egPhoneRegex = /^(010|011|012|015)[0-9]{8}$/;
  const egWithCountryRegex = /^20(10|11|12|15)[0-9]{8}$/;
  const egWithDoubleZero = /^0020(10|11|12|15)[0-9]{8}$/;
  return egPhoneRegex.test(cleaned) || egWithCountryRegex.test(cleaned) || egWithDoubleZero.test(cleaned);
};

/**
 * Normalizes Egyptian phone number into standard 11-digit string (e.g. 01012345678)
 */
export const normalizeEgyptianPhone = (phone: string): string => {
  if (!phone) return '';
  let digits = phone.trim().replace(/\D/g, '');
  if (digits.startsWith('0020')) {
    digits = '0' + digits.slice(4);
  } else if (digits.startsWith('20')) {
    digits = '0' + digits.slice(2);
  } else if (digits.length === 10 && /^(10|11|12|15)/.test(digits)) {
    digits = '0' + digits;
  }
  return digits;
};

/**
 * Maps raw Firebase Authentication error codes to localized Arabic AppError
 */
export const mapFirebaseAuthError = (error: { code?: string; message?: string }): AppError => {
  const code = error?.code || '';
  switch (code) {
    case 'auth/email-already-in-use':
      return createError('AUTH_EMAIL_ALREADY_IN_USE', error.message);
    case 'auth/invalid-email':
      return createError('AUTH_INVALID_EMAIL', error.message);
    case 'auth/weak-password':
      return createError('AUTH_WEAK_PASSWORD', error.message);
    case 'auth/invalid-credential':
      return createError('AUTH_INVALID_CREDENTIAL', error.message);
    case 'auth/wrong-password':
      return createError('AUTH_WRONG_PASSWORD', error.message);
    case 'auth/user-not-found':
      return createError('AUTH_USER_NOT_FOUND', error.message);
    case 'auth/user-disabled':
      return createError('AUTH_USER_DISABLED', error.message);
    case 'auth/too-many-requests':
      return createError('AUTH_TOO_MANY_REQUESTS', error.message);
    case 'auth/network-request-failed':
      return createError('AUTH_NETWORK_FAILED', error.message);
    default:
      return createError('UNKNOWN_ERROR', error.message || code);
  }
};

/**
 * Maps Firebase Functions / HttpsError to friendly Arabic AppError
 */
export const mapFunctionsError = (err: unknown): AppError => {
  const message = err instanceof Error ? err.message : String(err);
  if (message.includes('SEAT_ALREADY_BOOKED') || message.includes('already booked')) {
    return createError('SEAT_ALREADY_BOOKED');
  }
  if (message.includes('SEAT_HELD_BY_ANOTHER') || message.includes('held by another')) {
    return createError('SEAT_HELD_BY_ANOTHER');
  }
  if (message.includes('HOLD_EXPIRED') || message.includes('expired')) {
    return createError('HOLD_EXPIRED');
  }
  if (message.includes('TRIP_NOT_FOUND') || message.includes('not found')) {
    return createError('TRIP_NOT_FOUND');
  }
  if (message.includes('TRIP_FULL') || message.includes('full')) {
    return createError('TRIP_FULL');
  }
  if (message.includes('STUDENT_SUSPENDED') || message.includes('suspended')) {
    return createError('STUDENT_SUSPENDED');
  }
  if (message.includes('PROFILE_INCOMPLETE') || message.includes('profile')) {
    return createError('PROFILE_INCOMPLETE');
  }
  if (message.includes('unauthenticated') || message.includes('login')) {
    return createError('UNAUTHENTICATED_STUDENT');
  }
  return createError('UNKNOWN_ERROR', message);
};
